import { ComboBox, ComboBoxItem, ComboBoxProps } from '@florencelabs/combo-box';
import { MaterialSymbol } from '@florencelabs/icons';
import { useSearchParams } from 'next/navigation';
import { useState, ReactElement, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, useAsyncList } from 'react-stately';
import { DrawerAwareMobileCombobox } from '@provider-directory/components/drawer-aware-mobile-combobox/drawer-aware-mobile-combobox';
import {
  FilterDrawerFooter,
  useSearchFilters,
} from '@provider-directory/contexts/SearchFiltersContext';
import { cn } from '@provider-directory/functions/cn';
import { getUserLocation } from '@provider-directory/functions/location';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { useLanguage } from '@provider-directory/hooks/useLanguage';
import { getPlacePredictions } from '@provider-directory/services/places-autocomplete';
import { geocode } from '@provider-directory/services/places-geocoding';
import {
  currentLocationKey,
  LocationData,
} from '@provider-directory/types/locations.contracts';

interface ComboboxLocationItem {
  label: string;
  textValue?: string;
  className?: string;
  icon?: ReactElement;
  isDisabled?: boolean;
  preventAutoSelect?: boolean;
  testIdPrefix?: string | null;
}

interface LocationFilterProps
  extends Omit<
    ComboBoxProps<ComboboxLocationItem>,
    'children' | 'onSelectionChange'
  > {
  onSearch?: () => void;
  onSelectionChange: (locationData: LocationData | null) => void;
}

export const LocationFilter = (props: LocationFilterProps): ReactElement => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const searchParams = useSearchParams();
  const { language } = useLanguage();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { setIsOpen } = useSearchFilters();
  const autoSelectRef = useRef<
    ((e?: React.FocusEvent | React.KeyboardEvent) => void) | null
  >(null);

  const currentLocationOption = useMemo(
    () => ({
      className: 'text-blue-500',
      label: t('search.useCurrentLocation'),
      textValue: t('search.currentLocation'),
      icon: <MaterialSymbol name={'near_me'} size={16} aria-hidden={'true'} />,
      preventAutoSelect: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, language],
  );
  const address = searchParams.get('address');
  const [selectedKey, setSelectedKey] = useState<string | null>(
    address || null,
  );

  const {
    items = [],
    setFilterText,
    filterText,
    isLoading,
    reload,
  } = useAsyncList<ComboboxLocationItem>({
    async load({ filterText: text = '' }) {
      const query = selectedKey !== null && text === '' ? selectedKey : text;

      // for when a location is selected and the input is empty
      // happens on initial load
      const predictions = await getPlacePredictions(query);
      return {
        items: predictions.map((p) => ({ label: p.description })),
      };
    },
  });

  const noResultsOption = useMemo(
    () => ({
      label: `${t('search.noResults')} '${filterText}'`,
      icon: <MaterialSymbol name={'error'} aria-hidden={'true'} />,
      className: '!gap-2.5 !text-neutral-500',
      isDisabled: true,
      preventAutoSelect: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, filterText, language],
  );

  const displayItems = useMemo(() => {
    // for when input is empty and no location is selected
    if (filterText === '' && selectedKey === null) {
      return [currentLocationOption];
    }

    // when be have selected special location where selectedKey is not in the items and the input is empty
    if (
      filterText === '' &&
      selectedKey &&
      selectedKey !== t('search.useCurrentLocation') &&
      !items.some((item) => item.label === selectedKey)
    ) {
      return [{ label: selectedKey }];
    }
    // when selectkey is not matching the resulsts from the api
    // we pick the first result from the api
    if (
      selectedKey &&
      typeof selectedKey === 'string' &&
      filterText === selectedKey &&
      !items.some((item) => item.label === selectedKey)
    ) {
      return [{ label: selectedKey }];
    }
    if (items.length === 0) {
      if (filterText.length > 2 && !isLoading) {
        return [noResultsOption, currentLocationOption];
      }

      if (
        filterText.length > 0 ||
        selectedKey === t('search.useCurrentLocation')
      ) {
        return [currentLocationOption];
      }

      if (typeof selectedKey === 'string') {
        return [{ label: selectedKey }];
      }
    }
    // when doing a search
    if (filterText !== selectedKey) {
      return items;
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    items,
    selectedKey,
    filterText,
    isLoading,
    currentLocationOption,
    noResultsOption,
    address,
  ]);
  const handleLocationChange = async (locationKey: string | null) => {
    props.onSearch?.();
    setSelectedKey(locationKey);

    if (locationKey === t('search.useCurrentLocation')) {
      const location = await getUserLocation();
      if (location.status === 'SUCCESS') {
        props.onSelectionChange({
          label: currentLocationKey,
          value: currentLocationKey,
          lat: location.latitude,
          lng: location.longitude,
        });
      } else {
        alert(t('search.locationFailure'));
      }
      return;
    }

    if (typeof locationKey === 'string') {
      const results = await geocode({ address: locationKey });
      if (results.length > 0) {
        const location = {
          label: locationKey,
          value: locationKey,
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        props.onSelectionChange(location);
      }
    } else {
      props.onSelectionChange(null);
      reload();
    }
  };

  const handleAutoSelect = (autoSelectedKey: Key) => {
    const item = displayItems.find((i) => i.label === autoSelectedKey);
    if (!item || item.preventAutoSelect || selectedKey === item.label) return;

    setIsOpen(false);
    setIsMobileOpen(false);
    handleLocationChange(item.label);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  const commonProps: ComboBoxProps<ComboboxLocationItem> = {
    ...props,
    items: displayItems,
    selectedKey,
    onInputChange: setFilterText,
    onSelectionChange: (key) => handleLocationChange(key as string | null),
    onFocus: handleFocus,
    onAutoSelect: handleAutoSelect,
    children: (option: ComboboxLocationItem) => {
      return (
        <ComboBoxItem
          className={cn(
            '!max-w-none !whitespace-normal',
            { 'text-primary-color': option.label === selectedKey },
            option.className,
          )}
          id={option.label}
          key={option.label}
          data-testid={`location-filter-option-${option.label}`}
          icon={option.icon}
          isDisabled={option.isDisabled}
          value={option}
          textValue={option.textValue || option.label}
        >
          {option.label}
        </ComboBoxItem>
      );
    },
  };
  useEffect(() => {
    setFilterText(address || '');
    setSelectedKey(address || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, setSelectedKey]);

  return isDesktop ? (
    <ComboBox
      {...commonProps}
      testIdPrefix={'search-filter'}
      menuTrigger={'focus'}
    />
  ) : (
    <DrawerAwareMobileCombobox
      {...commonProps}
      testIdPrefix={'mobile-search-filter'}
      isOpen={isMobileOpen}
      onOpenChange={setIsMobileOpen}
      autoSelectRef={autoSelectRef}
      placeholder={t('search.submit')}
      popoverFooter={
        <FilterDrawerFooter
          isClearDisabled={!selectedKey}
          onClear={() => {
            handleLocationChange(null);
          }}
          onShowResults={() => {
            autoSelectRef.current?.();
          }}
        />
      }
    />
  );
};
